import { message, Spin } from "antd";
import SessionExpiredModal from "client/components/Common/SessionExpiredModal";
import { isUserError } from "client/services/apiFunctions";
import { SessionStorageKeys } from "common/enums";
import { FeatureFlagsType } from "common/types";
import { translate } from "common/utils";
import { observer } from "mobx-react";
import queryString from "query-string";
import React from "react";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import { COOKIE_NAME } from "../common/config";
import { CleaningLimitPoliciesState } from "./Data/CleaningLimitPoliciesStore";
import { DefaultFormats } from "./Data/DateTimeFormatStore";
import { defaultUnitState } from "./Data/DeafultUnits/DefaultUnitsStore";
import UserStore from "./Data/UserStore";
import { Auth } from "./scenes/Auth";
import {
  getFeatureFlags,
  getUserErrorMessage,
  logoutUser,
  sessionRecoveryService
} from "./services/api";

type State = {
  homeComponent: JSX.Element | null;
  loading: boolean;
  featureFlags?: FeatureFlagsType;
};

const LoadingWrapper = styled.div.attrs({
  id: "auth-loading-wrapper"
})`
  display: grid;
  justify-content: center;
  align-content: center;
  height: 100vh;
`;

const Authenticator = withRouter(
  observer(
    class AuthenticatorComponent extends React.Component<any, State> {
      constructor(props: any) {
        super(props);
        try {
          if (!sessionStorage.length) {
            this.deleteSessionCookie();
          }
          const currentFacility = sessionStorage.getItem(SessionStorageKeys.CURRENT_FACILITY);
          UserStore.isUserLoggedIn = !!currentFacility;
        } catch (e) {
          console.error(e);
          sessionStorage.removeItem(SessionStorageKeys.EMAIL);
        }

        this.state = {
          homeComponent: null,
          loading: false
        };

        if (UserStore.isUserLoggedIn) {
          this.handleLogin();
        }
      }

      componentDidMount() {
        this.setState(
          {
            loading: true
          },
          async () => {
            const { history } = this.props;
            try {
              const featureFlags = await getFeatureFlags();
              const ssoEnabled = featureFlags.SSO;

              if (ssoEnabled) {
                const { error } = queryString.parse(window.location.search);

                if (error) {
                  const errorObj = JSON.parse(error as string);
                  const errorMessage = isUserError(errorObj)
                    ? getUserErrorMessage(errorObj)
                    : errorObj.message;

                  message.config({ maxCount: 1 });
                  message.warning(errorMessage);
                  history.push(window.location.pathname);
                }

                // After SSO login, check for and restore any pending requests
                // that were saved before the SSO redirect
                if (UserStore.isUserLoggedIn && sessionRecoveryService.hasPendingStoredRequests()) {
                  this.restorePendingRequests();
                }
              }

              this.setState({
                featureFlags
              });
            } catch (e) {
              console.error(e);
            } finally {
              this.setState({
                loading: false
              });
            }
          }
        );
      }

      handleLogin = async () => {
        try {
          await DefaultFormats.setDefaultFormats();
        } catch (err) {
          console.log(err);
        }
        try {
          await defaultUnitState.setAllDefaultUnits();
        } catch (err) {
          console.log(err);
        }
        try {
          await UserStore.setCurrentUser();
        } catch (err) {
          console.log(err);
        }
        CleaningLimitPoliciesState.fetchCleaningLimitPolicies();

        await import("./scenes/Home").then(Component => {
          this.setState(prevState => {
            const { featureFlags } = prevState;
            return {
              homeComponent: (
                <Component.default
                  handleLogout={() => this.handleLogout()}
                  featureFlags={featureFlags!}
                />
              )
            };
          });
        });
        UserStore.isUserLoggedIn = true;
      };

      handleLogout = async (force = false) => {
        try {
          const isSSOEnabled = this.state.featureFlags?.SSO;
          const logoutUrl = this.state.featureFlags?.logoutUrl;
          if (!force) {
            await logoutUser(isSSOEnabled, logoutUrl);
          }

          UserStore.isUserLoggedIn = false;
          UserStore.clearSessionExpired();
          sessionStorage.clear();
          this.setState({
            homeComponent: null
          });
          this.deleteSessionCookie();
        } catch (err) {
          console.error(err);
        }
      };

      handleReauthenticate = async (password: string) => {
        await sessionRecoveryService.reAuthenticate(password);
      };

      handleSSORelogin = () => {
        const ssoProvider = this.state.featureFlags?.SSOProvider;
        if (ssoProvider) {
          sessionRecoveryService.handleSSORelogin(ssoProvider);
        } else {
          // Fallback: full logout if no SSO provider
          this.handleLogout(true);
        }
      };

      handleSessionExpiredCancel = () => {
        // User cancelled re-authentication: clear the session, then hard-redirect to
        // the login page. A full page reload (rather than an in-place SPA state swap)
        // guarantees no intermediate render of the torn-down Home page — which was
        // flashing the "No data to display" empty state before reaching Login.
        sessionRecoveryService.handleFullLogout();
        this.deleteSessionCookie();
        window.location.assign("/");
      };

      restorePendingRequests = async () => {
        try {
          const result = await sessionRecoveryService.restoreAndRetryQueuedRequests();
          if (!result) return;

          const { restoredCount, skippedUploadsCount } = result;

          if (restoredCount > 0) {
            message.success(translate("apiMessages:session-restored-pending-actions-completed"));
          }
          if (skippedUploadsCount > 0) {
            message.warning(translate("apiMessages:session-restored-uploads-skipped"));
          }
        } catch (error) {
          console.error("Failed to restore pending requests after SSO login:", error);
        }
      };

      deleteSessionCookie = () => {
        document.cookie = COOKIE_NAME + "=;expires=Thu, 01 Jan 1970 00:00:01 GMT;";
      };

      render() {
        const { homeComponent, loading, featureFlags } = this.state;
        const auth = <Auth handleLogin={this.handleLogin} />;
        const isSSO = featureFlags?.SSO || false;

        if (loading) {
          return (
            <LoadingWrapper>
              <Spin />
            </LoadingWrapper>
          );
        }

        return (
          <>
            {UserStore.isUserLoggedIn ? homeComponent : auth}
            <SessionExpiredModal
              visible={UserStore.isSessionExpired}
              isSSO={isSSO}
              onReauthenticate={this.handleReauthenticate}
              onSSOLogin={this.handleSSORelogin}
              onCancel={this.handleSessionExpiredCancel}
            />
          </>
        );
      }
    }
  )
);

export default Authenticator;
